import React, { useState, useEffect, useRef } from 'react'
import { Button, Input, Select, message } from 'antd'
import styled from 'styled-components'
import { suggestParameters } from './aiInterviewAPI'

const { TextArea } = Input

// Curated AI interviewer voices available on our ElevenLabs (Creator) tier.
// `id` is the ElevenLabs voice_id sent to TTS; `sample` is a pre-rendered clip
// (same sentence across voices) on OCI public storage for A/B preview.
const VOICE_SAMPLE_BASE =
  'https://bmv2bqg5gpcd.compat.objectstorage.ap-mumbai-1.oraclecloud.com/pl-uat-public-docs/ai-interview/voice-samples'
const VOICES = [
  // Indian voices — the default candidate base. Female:
  { id: 'CpLFIATEbkaZdJr01erZ', name: 'Payal', tag: 'Hindi · Female', desc: 'Warm, laid-back conversation', sample: `${VOICE_SAMPLE_BASE}/payal.mp3` },
  { id: 'RABOvaPec1ymXz02oDQi', name: 'Anika', tag: 'Hindi · Female', desc: 'Sweet, lively', sample: `${VOICE_SAMPLE_BASE}/anika.mp3` },
]
const DEFAULT_VOICE_ID = 'CpLFIATEbkaZdJr01erZ'
const { Option } = Select

const Page = styled.div`
  padding: 4px 0 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`
const Sub = styled.div`
  color: #64748b;
  font-size: 13px;
  margin-bottom: 18px;
`
const Section = styled.section`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 16px;
`
const SectionLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6366f1;
  font-weight: 600;
  margin-bottom: 4px;
`
const SectionTitle = styled.h2`
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #0f172a;
`
const Row = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.cols || '1fr 1fr'};
  gap: 12px;
  margin-bottom: 12px;
`
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`
const Label = styled.label`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`
const PolicyCard = styled.div`
  border: 2px solid ${(p) => (p.active ? '#10b981' : '#e2e8f0')};
  background: ${(p) => (p.active ? 'rgba(16,185,129,0.05)' : '#fff')};
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  text-align: center;
  transition: all 150ms;
  &:hover { border-color: ${(p) => (p.active ? '#10b981' : '#94a3b8')}; }
`
const PolicyTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
`
const PolicyDesc = styled.div`
  font-size: 12px;
  color: #64748b;
`
const ParamCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 8px;
  display: grid;
  grid-template-columns: 2fr 3fr 80px 36px;
  gap: 10px;
  align-items: center;
  background: #fafbfc;
`
const RemoveBtn = styled.button`
  border: none;
  background: transparent;
  color: #ef4444;
  cursor: pointer;
  font-size: 18px;
  &:hover { color: #b91c1c; }
`

const DEFAULT_RUBRIC = [
  { id: 'communication', name: 'Communication skills', weight: 30 },
  { id: 'role_fit', name: 'Role fitment', weight: 30 },
  { id: 'smartness', name: 'Sales ability / Street smartness', weight: 40 },
]

const newParam = () => ({
  id: `param_${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  description: '',
  weight: 10,
  min_pass_rating: 3,
})

// When mounted inside the parent assessment-creation page, lift state via setAssessmentForms.
// When mounted standalone, just keeps local state.
const CreateAIInterview = ({ formId, setAssessmentForms, assessmentForms }) => {
  const existing = assessmentForms?.forms?.[formId]?.aiInterview || null

  const [form, setForm] = useState(existing?.form || {
    jobRole: '',
    seniority: 'Mid',
    industryDomain: '',
    region: 'India',
    jobDescription: '',
    interviewDuration: 900,
    maxQuestions: 8,
    resumePolicy: 'optional',
    language: 'English',
    responderLanguage: 'English',
    probingStyle: 'neutral',
    difficultyCurve: 'flat',
    scoringGuidance: '',
    questionGuidance: '',
    voiceId: DEFAULT_VOICE_ID,
  })
  const [playingVoice, setPlayingVoice] = useState(null)
  const sampleAudioRef = useRef(null)

  const playSample = (v) => {
    try {
      if (sampleAudioRef.current) {
        sampleAudioRef.current.pause()
        sampleAudioRef.current = null
      }
      if (playingVoice === v.id) { setPlayingVoice(null); return }
      const a = new Audio(v.sample)
      a.onended = () => setPlayingVoice(null)
      a.onerror = () => { setPlayingVoice(null); message.error('Could not play sample') }
      sampleAudioRef.current = a
      a.play()
      setPlayingVoice(v.id)
    } catch (e) {
      setPlayingVoice(null)
    }
  }
  const [parameters, setParameters] = useState(existing?.parameters || [])
  const [suggesting, setSuggesting] = useState(false)

  // Sync up to parent every time form or parameters change
  useEffect(() => {
    if (!formId || !setAssessmentForms) return
    setAssessmentForms((prev) => ({
      ...prev,
      forms: {
        ...prev.forms,
        [formId]: {
          ...(prev.forms?.[formId] || {}),
          aiInterview: { form, parameters, conversationRubric: DEFAULT_RUBRIC },
        },
      },
    }))
  }, [form, parameters, formId])

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const handleSuggest = async () => {
    if (!form.jobRole) return message.error('Enter a job role first')
    setSuggesting(true)

    const MAX_CLIENT_RETRIES = 3
    let lastError = null

    const isTransientStatus = (status) =>
      !status || status === 408 || status === 502 || status === 503 || status === 504

    for (let attempt = 1; attempt <= MAX_CLIENT_RETRIES; attempt++) {
      try {
        if (attempt > 1) {
          message.info(`Retrying parameter generation (attempt ${attempt}/${MAX_CLIENT_RETRIES})...`)
        }
        const { data } = await suggestParameters({
          jobRole: form.jobRole,
          seniority: form.seniority,
          industryDomain: form.industryDomain || null,
          jobDescription: form.jobDescription || null,
          region: form.region,
        })
        const raw = data?.data?.parameters || data?.parameters || []
        const list = Array.isArray(raw) ? raw.slice(0, 5) : []
        if (list.length === 0) {
          lastError = new Error('Empty parameters response')
          if (attempt < MAX_CLIENT_RETRIES) {
            await new Promise((r) => setTimeout(r, 1000 * attempt))
            continue
          }
          break
        }
        setParameters(list)
        message.success(`Suggested ${list.length} parameters`)
        setSuggesting(false)
        return
      } catch (e) {
        lastError = e
        const status = e?.response?.status
        const isTimeout = e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT' || /timeout/i.test(e?.message || '')
        console.warn(`suggestParameters attempt ${attempt} failed:`, status, e?.message)
        if (!isTransientStatus(status) && !isTimeout) break
        if (attempt < MAX_CLIENT_RETRIES) {
          await new Promise((r) => setTimeout(r, 1000 * attempt))
        }
      }
    }

    setSuggesting(false)
    message.error(
      lastError?.response?.data?.message ||
      lastError?.message ||
      'Failed to suggest parameters. Please try again.'
    )
  }

  const updateParam = (idx, patch) => {
    setParameters((ps) => ps.map((p, i) => (i === idx ? { ...p, ...patch } : p)))
  }
  const removeParam = (idx) => setParameters((ps) => ps.filter((_, i) => i !== idx))
  const addParam = () => setParameters((ps) => [...ps, newParam()])
  const weightSum = parameters.reduce((s, p) => s + Number(p.weight || 0), 0)

  return (
    <Page>
      <Sub>
        Conversational, adaptive voice interview. Define the role, set the resume policy,
        let the AI suggest parameters for evaluation. Use the candidate assignment below to send it out.
      </Sub>

      {/* CONTEXT */}
      <Section>
        <SectionLabel>Step 1 of 4</SectionLabel>
        <SectionTitle>Role context</SectionTitle>
        <Row>
          <Field>
            <Label>Job role *</Label>
            <Input value={form.jobRole} onChange={(e) => set('jobRole', e.target.value)}
              placeholder="e.g. FMCG Field Sales Rep — Tier 2 cities" />
          </Field>
          <Field>
            <Label>Seniority *</Label>
            <Select value={form.seniority} onChange={(v) => set('seniority', v)}>
              <Option value="Fresher">Fresher</Option>
              <Option value="Junior">Junior</Option>
              <Option value="Mid">Mid</Option>
              <Option value="Senior">Senior</Option>
              <Option value="Lead">Lead</Option>
            </Select>
          </Field>
        </Row>
        <Row>
          <Field>
            <Label>Industry / Domain</Label>
            <Input value={form.industryDomain} onChange={(e) => set('industryDomain', e.target.value)}
              placeholder="e.g. Consumer Goods" />
          </Field>
          <Field>
            <Label>Region</Label>
            <Input value={form.region} onChange={(e) => set('region', e.target.value)} />
          </Field>
        </Row>
        <Field>
          <Label>Job description (optional)</Label>
          <TextArea rows={4} value={form.jobDescription}
            onChange={(e) => set('jobDescription', e.target.value)}
            placeholder="Paste the JD here. The AI uses this to suggest evaluation parameters." />
        </Field>
        <Field>
          <Label>Question guidance (optional)</Label>
          <Sub style={{ marginBottom: 8 }}>
            Notes on how the AI should ask questions — sample questions, topics to focus on, or areas to
            probe. This is added to the prompt used to generate every question (the AI adapts your samples,
            it won't always ask them verbatim). You can add up to <b>12 questions</b> here — if you add more,
            not all of them will be asked (the interview also uses turns for the intro and follow-ups).
          </Sub>
          <TextArea rows={4} value={form.questionGuidance}
            onChange={(e) => set('questionGuidance', e.target.value)}
            placeholder="e.g. Always include one scenario about handling an angry customer. Ask at least one question on SQL joins. Keep questions practical, not theoretical." />
        </Field>
      </Section>

      {/* RESUME POLICY */}
      <Section>
        <SectionLabel>Step 2 of 4</SectionLabel>
        <SectionTitle>Resume policy</SectionTitle>
        <Row cols="repeat(3, 1fr)">
          {[
            { v: 'mandatory', t: 'Mandatory', d: 'Candidate must upload to start.' },
            { v: 'optional', t: 'Optional', d: 'They can upload or skip — AI uses it if available.' },
            { v: 'not_required', t: 'Not required', d: 'Step is hidden. Pure conversational interview.' },
          ].map((p) => (
            <PolicyCard key={p.v} active={form.resumePolicy === p.v} onClick={() => set('resumePolicy', p.v)}>
              <PolicyTitle>{p.t}</PolicyTitle>
              <PolicyDesc>{p.d}</PolicyDesc>
            </PolicyCard>
          ))}
        </Row>
      </Section>

      {/* EVALUATION PARAMETERS */}
      <Section>
        <SectionLabel>Step 3 of 4</SectionLabel>
        <SectionTitle>Evaluation parameters</SectionTitle>
        <Sub style={{ marginBottom: 12 }}>
          Click <b>Suggest from role</b> — the AI proposes 5 role-specific parameters. Edit weights, rename, or remove. Weights must sum to 100. Max 5 parameters.
        </Sub>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Button type="primary" loading={suggesting} onClick={handleSuggest}>
            ✨ Suggest from role
          </Button>
          <Button onClick={addParam} disabled={parameters.length >= 5}>
            {parameters.length >= 5 ? 'Max 5 parameters' : '+ Add manual parameter'}
          </Button>
          <div style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 13, color: weightSum === 100 ? '#10b981' : '#ef4444' }}>
            Weight sum: <b>{weightSum}</b> / 100
          </div>
        </div>
        {parameters.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 18, border: '1px dashed #cbd5e1', borderRadius: 8 }}>
            No parameters yet — suggest from role or add manually.
          </div>
        )}
        {parameters.map((p, idx) => (
          <ParamCard key={p.id || idx}>
            <Input value={p.name} placeholder="Parameter name"
              onChange={(e) => updateParam(idx, { name: e.target.value })} />
            <Input value={p.description} placeholder="What this measures"
              onChange={(e) => updateParam(idx, { description: e.target.value })} />
            <Input type="number" min={1} max={100} value={p.weight}
              onChange={(e) => updateParam(idx, { weight: Number(e.target.value) || 0 })}
              suffix="%" />
            {/* min_pass_rating dropdown removed from the form — it read like a
                question count to admins and was confusing. The interview is
                hard-capped at 8 questions and the AI handles distribution
                across parameters dynamically, so admins don't need to pick a
                pass threshold either. We still send min_pass_rating: 3 on the
                wire so the backend scorer's existing logic keeps working. */}
            <RemoveBtn onClick={() => removeParam(idx)} title="Remove">×</RemoveBtn>
          </ParamCard>
        ))}
        <Field style={{ marginTop: 16 }}>
          <Label>Scoring guidance (optional)</Label>
          <Sub style={{ marginBottom: 8 }}>
            Notes on how the AI should evaluate the final score — e.g. "prioritise problem-solving over
            polish", "penalise vague answers", "reward candidates who ask clarifying questions". This is
            added to the scoring prompt when the whole interview is assessed.
          </Sub>
          <TextArea rows={3} value={form.scoringGuidance}
            onChange={(e) => set('scoringGuidance', e.target.value)}
            placeholder="e.g. Give more weight to depth of reasoning than to fluency. Be strict on candidates who don't give concrete examples." />
        </Field>
      </Section>

      {/* INTERVIEWER TONE */}
      <Section>
        <SectionLabel>Step 4 of 4</SectionLabel>
        <SectionTitle>Interviewer tone</SectionTitle>
        <Row cols="repeat(4, 1fr)">
          <Field>
            <Label>AI speaks in</Label>
            <Select value={form.language} onChange={(v) => set('language', v)}>
              <Option value="English">English</Option>
              <Option value="Hinglish">Hinglish</Option>
              <Option value="Hindi">Hindi</Option>
              <Option value="Tamil">Tamil</Option>
              <Option value="Telugu">Telugu</Option>
              <Option value="Kannada">Kannada</Option>
              <Option value="Malayalam">Malayalam</Option>
              <Option value="Bengali">Bengali</Option>
              <Option value="Gujarati">Gujarati</Option>
              <Option value="Marathi">Marathi</Option>
              <Option value="Punjabi">Punjabi</Option>
              <Option value="Urdu">Urdu</Option>
            </Select>
          </Field>
          <Field>
            <Label>Candidate responds in</Label>
            <Select value={form.responderLanguage} onChange={(v) => set('responderLanguage', v)}>
              <Option value="English">English</Option>
              <Option value="Hinglish">Hinglish</Option>
              <Option value="Hindi">Hindi</Option>
              <Option value="Tamil">Tamil</Option>
              <Option value="Telugu">Telugu</Option>
              <Option value="Kannada">Kannada</Option>
              <Option value="Malayalam">Malayalam</Option>
              <Option value="Bengali">Bengali</Option>
              <Option value="Gujarati">Gujarati</Option>
              <Option value="Marathi">Marathi</Option>
              <Option value="Punjabi">Punjabi</Option>
              <Option value="Urdu">Urdu</Option>
            </Select>
          </Field>
          <Field>
            <Label>Probing style</Label>
            <Select value={form.probingStyle} onChange={(v) => set('probingStyle', v)}>
              <Option value="gentle">Gentle</Option>
              <Option value="neutral">Neutral</Option>
              <Option value="challenger">Challenger</Option>
            </Select>
          </Field>
          <Field>
            <Label>Difficulty curve</Label>
            <Select value={form.difficultyCurve} onChange={(v) => set('difficultyCurve', v)}>
              <Option value="flat">Flat</Option>
              <Option value="escalating">Escalating</Option>
            </Select>
          </Field>
        </Row>
        <Row cols="1fr 1fr 2fr">
          <Field>
            <Label>Max duration (minutes)</Label>
            <Input type="number" min={10} max={30}
              value={Math.round(form.interviewDuration / 60)}
              onChange={(e) => {
                const mins = Math.min(30, Math.max(10, Number(e.target.value) || 15))
                set('interviewDuration', mins * 60)
              }} />
            <span style={{ fontSize: 11, color: '#94a3b8' }}>10–30 minutes (default 15) — the AI ends sooner when the interview converges.</span>
          </Field>
          <Field>
            <Label>Number of questions</Label>
            <Input type="number" min={8} max={15}
              value={form.maxQuestions ?? 8}
              onChange={(e) => {
                const n = Math.min(15, Math.max(8, Math.round(Number(e.target.value) || 8)))
                set('maxQuestions', n)
              }} />
            <span style={{ fontSize: 11, color: '#94a3b8' }}>8–15 (default 8) — total questions including the intro.</span>
          </Field>
        </Row>

        <Field style={{ marginTop: 16 }}>
          <Label>Interviewer voice</Label>
          <Sub style={{ marginBottom: 10 }}>
            Pick the voice the AI interviewer speaks in. Click ▶ to hear a sample.
          </Sub>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {VOICES.map((v) => {
              const active = form.voiceId === v.id
              return (
                <div
                  key={v.id}
                  onClick={() => set('voiceId', v.id)}
                  style={{
                    border: `1.5px solid ${active ? '#494FDF' : '#e2e8f0'}`,
                    background: active ? 'rgba(73,79,223,0.05)' : '#fff',
                    borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}
                >
                  <span
                    role="button"
                    aria-label={`Play ${v.name} sample`}
                    onClick={(e) => { e.stopPropagation(); playSample(v) }}
                    style={{
                      flex: 'none', width: 32, height: 32, borderRadius: '50%',
                      display: 'grid', placeItems: 'center', background: '#494FDF', color: '#fff',
                      fontSize: 13,
                    }}
                  >
                    {playingVoice === v.id ? '❚❚' : '▶'}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>
                      {v.name} {active && <span style={{ color: '#494FDF', fontSize: 12 }}>✓</span>}
                    </div>
                    <div style={{ fontSize: 11.5, color: '#64748b' }}>{v.tag} · {v.desc}</div>
                  </div>
                </div>
              )
            })}
          </div>
        </Field>
      </Section>
    </Page>
  )
}

export default CreateAIInterview
